import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import Utils from 'models/helper/Utils';

/**
 * SequencerTimelineExportHelper - Handles timeline data extraction for export functionality
 * 
 * This helper encapsulates the logic for extracting timeline data from the sequencer
 * renderer's scheduling information, converting it to normalized timeline formats
 * suitable for export to EDL, FCPXML, and other timeline formats.
 */
const SequencerTimelineExportHelper = Backbone.Model.extend({
    idAttribute: 'id',

    /**
     * Extract timeline data from scheduling info using existing rendering pipeline
     * 
     * @param {Object} renderer - The sequencer renderer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<Array>} Array of timeline clip objects
     */
    extractTimelineDataFromRenderer: async function(renderer, startNode, endNode, options) {
        try {
            // Use the new public method to extract scheduling info for export
            const schedulingInfoArray = await renderer.extractSchedulingInfoForExport(
                startNode,
                endNode,
                options
            );

            // Convert the scheduling info to our normalized timeline format
            // Pass selection info to help determine if normalization is needed
            const isSelectionExport = startNode !== null || endNode !== null;
            return this._convertSchedulingInfoToTimelineClips(schedulingInfoArray, options, isSelectionExport);

        } catch (error) {
            console.error('Error extracting timeline data from renderer:', error);
            throw new Error('Failed to extract timeline data from renderer: ' + error.message);
        }
    },

    /**
     * Convert SequencerNodeSchedulingInfo array to normalized timeline clip format
     * 
     * @param {Array} schedulingInfoArray - Array of SequencerNodeSchedulingInfo objects
     * @param {Object} options - Export options
     * @param {Boolean} isSelectionExport - True if this is a selection export, false for full project
     * @returns {Array} Array of timeline clip objects
     */
    _convertSchedulingInfoToTimelineClips: function(schedulingInfoArray, options, isSelectionExport = false) {
        if (!schedulingInfoArray || !_.isArray(schedulingInfoArray)) {
            return [];
        }

        const timelineClips = [];

        schedulingInfoArray.forEach((nodeInfo, index) => {
            try {
                // Handle both regular nodes and wrap nodes (MergedIncompleteWrapSequencerNodeSchedulingInfo)
                let sequencerNode = nodeInfo.getSequencerNodeReference();
                
                // Special handling for wrap nodes (background music)
                if (!sequencerNode && nodeInfo.getStartWrapSequencerNodeReference) {
                    sequencerNode = nodeInfo.getStartWrapSequencerNodeReference();
                }
                
                // Skip nodes without valid references
                if (!sequencerNode) {
                    return;
                }

                // Get audio buffer to extract channel count
                const audioBuffer = nodeInfo.getAudioBuffer();
                const channels = audioBuffer ? audioBuffer.numberOfChannels : 2; // Default to stereo if no buffer

                // Create timeline clip object
                const clip = {
                    // Timeline positioning
                    startTimeInSeconds: nodeInfo.getStartTimeOffset() / 1000,
                    endTimeInSeconds: nodeInfo.getEndTimeOffset() / 1000,
                    durationInSeconds: nodeInfo.getDuration() / 1000,

                    // Source media info - handle both regular and wrap nodes
                    sourceMediaUrl: this._getMediaUrlFromNodeInfo(nodeInfo),
                    fileName: this._extractFileName(this._getMediaUrlFromNodeInfo(nodeInfo)),
                    reelName: this._generateReelName(nodeInfo, index, options),

                    // Source segment timing (within original file)
                    sourceStartInSeconds: nodeInfo.getStartTimeOffsetForSourceBuffer() / 1000,
                    sourceEndInSeconds: nodeInfo.getEndTimeOffsetForSourceBuffer() / 1000,
                    sourceOffsetTimecode: this._calculateSourceOffsetTimecode(nodeInfo, options),

                    // Media properties
                    hasVideo: nodeInfo.isSequencerNodeReferenceHasVideo(),
                    videoWidth: nodeInfo.getCachedVideoWidth() || nodeInfo.getSequencerNodeReferenceVideoWidth(),
                    videoHeight: nodeInfo.getCachedVideoHeight() || nodeInfo.getSequencerNodeReferenceVideoHeight(),
                    frameRate: options.frameRate,

                    // Audio properties
                    channels: channels,                         // Number of audio channels (1=mono, 2=stereo)
                    audioFilters: nodeInfo.getAllUserDefinedAudioFilters(),

                    // Node metadata
                    nodeType: this._getNodeType(sequencerNode),
                    isDeleted: Utils.getInstance().isDeletedSequencerNodeInstance(sequencerNode),

                    // Word timestamp data (for subtitles)
                    wordTimestamps: options.includeWordTimestamps ?
                        this._extractWordTimestamps(nodeInfo) : null
                };

                timelineClips.push(clip);

            } catch (error) {
                console.warn('Error processing node at index', index, ':', error);
                // Continue processing other nodes
            }
        });

        // Normalize timeline for selection exports only
        // Selection exports should start at 00:00:00:00 regardless of their position in the full project
        if (isSelectionExport && timelineClips.length > 0) {
            const firstClipStartTime = Math.min(...timelineClips.map(clip => clip.startTimeInSeconds));
            if (firstClipStartTime > 0) {
                // Normalize selection export to start at 0
                timelineClips.forEach(clip => {
                    clip.startTimeInSeconds -= firstClipStartTime;
                    clip.endTimeInSeconds -= firstClipStartTime;
                    // Note: We preserve sourceStartInSeconds and sourceEndInSeconds 
                    // as they represent timing within the original media files
                });
            }
        }

        return timelineClips;
    },

    /**
     * Get media URL from node info, handling both regular and wrap nodes
     */
    _getMediaUrlFromNodeInfo: function(nodeInfo) {
        // Try regular node method first
        if (nodeInfo.getAudioSegmentMediaUrl) {
            const url = nodeInfo.getAudioSegmentMediaUrl();
            if (url) {
                return url;
            }
        }
        
        // For wrap nodes, get URL from the start wrap node's audio segment
        if (nodeInfo.getStartWrapSequencerNodeReference) {
            const startWrapNode = nodeInfo.getStartWrapSequencerNodeReference();
            if (startWrapNode && startWrapNode.getAudioSegment) {
                const audioSegment = startWrapNode.getAudioSegment();
                if (audioSegment && audioSegment.getAudioUrl) {
                    return audioSegment.getAudioUrl();
                }
            }
        }
        
        return null;
    },

    /**
     * Extract filename from media URL
     */
    _extractFileName: function(mediaUrl) {
        if (!mediaUrl) {
            return 'unknown_file';
        }

        try {
            // Extract filename from URL
            const url = new URL(mediaUrl);
            const pathname = url.pathname;
            const filename = pathname.split('/').pop();
            
            // If no filename found, generate one
            if (!filename || filename === '') {
                return 'trebble_clip_' + Date.now();
            }

            return filename;
        } catch (error) {
            // If URL parsing fails, try simple string extraction
            const parts = mediaUrl.split('/');
            return parts[parts.length - 1] || 'trebble_clip_' + Date.now();
        }
    },

    /**
     * Generate reel name for the clip
     */
    _generateReelName: function(nodeInfo, index, options) {
        const sequencerNode = nodeInfo.getSequencerNodeReference();
        
        if (nodeInfo.isSequencerNodeReferenceHasVideo()) {
            return options.reelNamePrefix || 'V'; // Video reel
        } else {
            return 'A'; // Audio reel
        }
    },

    /**
     * Calculate source offset timecode
     */
    _calculateSourceOffsetTimecode: function(nodeInfo, options) {
        const sourceStartInSeconds = nodeInfo.getStartTimeOffsetForSourceBuffer() / 1000;
        return this._convertSecondsToTimecode(sourceStartInSeconds, options.frameRate);
    },

    /**
     * Convert seconds to SMPTE timecode format (HH:MM:SS:FF)
     */
    _convertSecondsToTimecode: function(seconds, frameRate) {
        frameRate = frameRate || 25;
        
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        const frames = Math.floor((seconds % 1) * frameRate);

        return [
            hours.toString().padStart(2, '0'),
            minutes.toString().padStart(2, '0'),
            secs.toString().padStart(2, '0'),
            frames.toString().padStart(2, '0')
        ].join(':');
    },

    /**
     * Get node type string
     */
    _getNodeType: function(sequencerNode) {
        const utils = Utils.getInstance();
        
        if (utils.isWordSequencerNodeInstance(sequencerNode)) {
            return 'word';
        } else if (utils.isAudioSequencerNodeInstance(sequencerNode)) {
            return 'audio';
        } else if (utils.isVideoSequencerNodeInstance(sequencerNode)) {
            return 'video';
        } else if (utils.isPauseSequencerNodeInstance(sequencerNode)) {
            return 'pause';
        } else if (utils.isDeletedSequencerNodeInstance(sequencerNode)) {
            return 'deleted';
        } else if (utils.isStartMusicWrapSequencerNodeInstance(sequencerNode)) {
            return 'wrap_music';
        } else if (utils.isEndMusicWrapSequencerNodeInstance(sequencerNode)) {
            return 'wrap_music_end';
        }
        
        return 'unknown';
    },

    /**
     * Extract word timestamp information
     */
    _extractWordTimestamps: function(nodeInfo) {
        const sequencerNode = nodeInfo.getSequencerNodeReference();
        const utils = Utils.getInstance();
        
        if (!utils.isWordSequencerNodeInstance(sequencerNode)) {
            return null;
        }

        try {
            return {
                word: sequencerNode.getWordText ? sequencerNode.getWordText() : '',
                startTime: nodeInfo.getStartTimeOffset() / 1000,
                endTime: nodeInfo.getEndTimeOffset() / 1000,
                speaker: sequencerNode.getSpeakerId ? sequencerNode.getSpeakerId() : null,
                confidence: sequencerNode.getConfidenceScore ? sequencerNode.getConfidenceScore() : null
            };
        } catch (error) {
            console.warn('Error extracting word timestamps:', error);
            return null;
        }
    },

    /**
     * Create validator function to exclude deleted nodes
     * Note: This method is also used by SequencerExportHelper.js
     */
    _createNonDeletedNodeValidator: function() {
        return function(sequencerNode, nodeSchedulingInfo) {
            return !Utils.getInstance().isDeletedSequencerNodeInstance(sequencerNode);
        };
    }

});

// Create singleton instance
const SequencerTimelineExportHelperInstance = new SequencerTimelineExportHelper();

// Export singleton instance
export default {
    getInstance: function() {
        return SequencerTimelineExportHelperInstance;
    }
};